import React, { useState, useEffect, useMemo } from 'react';
import {
  Icon,
  HouseLine,
  Presentation,
  PencilSimple,
  ChartBar,
  FolderOpen,
  Users,
  UserCirclePlus,
  QrCode,
  DownloadSimple,
} from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';

import { SideNavMainBtn, SideNavSubBtn, TabPanel } from '..';

import {
  trackOpenProject,
  trackEditProject,
  trackClickProjectAnalytics,
} from '../../../../analytics';

import { URL } from '../../../../constants';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { useProjectStore } from '../../../../stores/project';

import { getUrl } from '../../../../utils';

import { QRCode, downloadQRCode } from '../../../custom';

const ProjectSideNav = () => {
  const { eventId = '' } = useParams();

  const pathname = window.location.pathname;

  const { currentProject } = useProjectStore();

  const [openedPanelId, setOpenedPanelId] = useState<string | null>(null);

  const { isSample } = useSampleProjectData(eventId);

  const { checkInPageLink, isComplete } = useMemo(() => {
    return {
      checkInPageLink: getUrl(URL.CHECK_IN, eventId, undefined, true) ?? '',
      isComplete: currentProject?.stage?.toLowerCase() === 'generation',
    };
  }, [eventId, currentProject?.stage]);

  const assetsBtns: {
    Icon: Icon;
    label: string;
    url?: string;
    onClick?: () => void;
    isNewTab?: boolean;
    ExtraIcon?: Icon;
    show?: boolean;
  }[] = [
    {
      Icon: Users,
      label: 'Hub Page',
      url: getUrl(URL.SHARE, eventId),
      isNewTab: true,
      show: isComplete,
    },
    {
      Icon: UserCirclePlus,
      label: 'Faces Check-In',
      url: checkInPageLink,
      isNewTab: true,
    },
    {
      Icon: QrCode,
      label: 'Download Check-In QR',
      onClick: () =>
        downloadQRCode(eventId, currentProject?.name ?? '', 'Side Nav'),
      ExtraIcon: DownloadSimple,
    },
  ];

  const btns: {
    id: string;
    icon: Icon;
    label: string;
    url?: string;
    onClick?: () => void;
    content?: any;
    isHide?: boolean;
  }[] = [
    {
      id: 'vb',
      icon: Presentation,
      label: 'Video Builder',
      url: getUrl(URL.VIDEO_BUILDER, eventId),
      onClick: () => trackOpenProject('side_nav', currentProject!),
    },
    {
      id: 'edit',
      icon: PencilSimple,
      label: 'Edit',
      url: getUrl(URL.PROJECT_SETTINGS, eventId),
      onClick: () => trackEditProject('Side Nav'),
      isHide: isSample,
    },
    {
      id: 'analytics',
      icon: ChartBar,
      label: 'Analytics',
      url: getUrl(URL.PROJECT_ANALYTICS, eventId),
      onClick: () => trackClickProjectAnalytics('Side Nav'),
      isHide: isSample,
    },
    {
      id: 'assets',
      icon: FolderOpen,
      label: 'Assets',
      content: (
        <React.Fragment>
          <div className="font-bold mb-2">Project Assets and Quick Links</div>

          {assetsBtns.map(
            (btn) =>
              btn.show !== false && <SideNavSubBtn key={btn.label} {...btn} />,
          )}

          {!isComplete && (
            <React.Fragment>
              <hr className="my-2 border-grey-200 opacity-100" />

              <div className="text-sm italic">
                Hub Page will be available once some videos are generated.
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      ),
      isHide: isSample,
    },
  ];

  // handle showing and hiding tab panel
  useEffect(() => {
    const handleHover = (event: any) => {
      let isActionDone = false;

      btns.forEach((btn) => {
        const tabElement = document.getElementById(
          `project-sidenav-tab-${btn.id}`,
        );

        if (tabElement?.contains(event.target)) {
          if (!openedPanelId || openedPanelId !== btn.id)
            setOpenedPanelId(btn.id);

          isActionDone = true;
        }
      });

      if (!isActionDone && !!openedPanelId)
        btns.forEach((btn) => {
          const panelElement = document.getElementById(
            `project-sidenav-tabpanel-${btn.id}`,
          );

          if (openedPanelId === btn.id && !panelElement?.contains(event.target))
            setOpenedPanelId(null);
        });
    };

    document.addEventListener('mouseover', handleHover);
    return () => document.removeEventListener('mouseover', handleHover);
  }, [openedPanelId]);

  return (
    <React.Fragment>
      <SideNavMainBtn id="home" Icon={HouseLine} url={URL.HOME} label="Home" />

      <div className="flex flex-col gap-4">
        {btns.map(
          (btn) =>
            !btn.isHide && (
              <SideNavMainBtn
                key={btn.id}
                id={btn.id}
                Icon={btn.icon}
                url={btn.url}
                onClick={btn.onClick}
                label={btn.label}
                isCurrent={!!btn.url && pathname.includes(btn.url)}
              />
            ),
        )}
      </div>

      <div className="absolute top-0 left-[calc(var(--page-spacing)_+_3.5rem)] h-screen flex items-center">
        {btns.map(
          (btn) =>
            btn.content && (
              <TabPanel key={btn.id} id={btn.id} openedPanelId={openedPanelId}>
                {btn.content}
              </TabPanel>
            ),
        )}
      </div>

      <QRCode eventId={eventId} link={checkInPageLink} />
    </React.Fragment>
  );
};

export default ProjectSideNav;
